import { rankWith, schemaMatches } from "@jsonforms/core";
import { withJsonFormsControlProps } from "@jsonforms/react";
import { useTranslation } from "react-i18next";
import SFSelect from "../../select";
import SFGenericSearch from "../../typeahead/genericSearch";

// Function (helper) to prepare data for oneOf or enum schemas, by converting enum to oneOf format
const getDropDownOptions = (schema) => {
  if (schema.oneOf) { // if oneOf is defined, return it as is
    return schema.oneOf;
  } else if (schema.enum) { // if enum is defined, map it to oneOf format for dropdown usage
    return schema.enum.map((value, id) => ({
      const: value,
      title: schema.enumNames ? schema.enumNames[id] : value,
    }));
  }
};

const SelectRenderer = ({
  data = "",
  handleChange,
  path,
  uischema,
  schema,
  errors,
  //this property is coming from the json forms component.
  enabled = true,
}) => {
  const { t } = useTranslation();
  const values = getDropDownOptions(schema); // formely schema.oneOf, now supports enum as well but in the format of oneOf {const, title} as converted with the above helper function

  const onChangeFilter = (e) => {
    const id = values.find((val) => val.title === e.value)?.const;
    handleChange(path, id);
  };

  return (
    <SFSelect
      disabled={!enabled}
      name={uischema.name ?? ""}
      helperText={errors}
      hasError={errors?.length > 1}
      placeholder={uischema.placeholder ?? t("components.formBuilder.selectPlaceholder")}
      selectedValue={values?.find((val) => val.const === data)?.title}
      onChangeFilter={onChangeFilter}
      values={values?.map((option) => option.title)}
      {...(uischema?.options?.optionFormatter && {
        optionFormatter: uischema.options.optionFormatter,
      })}
      {...(uischema?.options?.valueFormatter && {
        valueFormatter: uischema.options.valueFormatter,
      })}
      label={uischema.i18nKey ? t(uischema.i18nKey) : uischema.label}
      size="medium"
      required={schema.isRequired}
      disabledValues={uischema.options?.disabledValues}
    />
  );
};

export const SelectTester = rankWith(
  101,
  schemaMatches((schema) => schema && (schema.oneOf || schema.enum))
);

const SelectWithSearch = ({
  data = "",
  handleChange,
  path,
  uischema,
  schema,
  required,
  errors,
}) => {
  const { t } = useTranslation();
  const values = getDropDownOptions(schema); // formely schema.oneOf, now supports enum as well but in the format of oneOf {const, title} as converted with the above helper function
  const addNewOption = schema.addNewOption === true;

  const onChangeFilter = (option) => {
    handleChange(path, option ? option.id : "");
  };

  const getDefaultValue = () => {
    let defaultVal = values?.find((val) => val.const === data);
    if (defaultVal && defaultVal.const.length > 0) {
      return [defaultVal.title];
    }
    if (addNewOption) {
      return [data];
    }
    return [];
  };

  return (
    <SFGenericSearch
      onSelect={onChangeFilter}
      errors={errors}
      source={
        values?.map((option) => ({
          value: option.title,
          id: option.const,
        })) ?? []
      }
      label={uischema.i18nKey ? t(uischema.i18nKey) : uischema.label}
      hasSearchIcon
      customIcon = {uischema?.options?.customIcon}
      isCopilotField = {uischema?.options?.isCopilotField}
      selectedValues={getDefaultValue()}
      required={required}
      disabled={schema.disabled}
      addNewOption={addNewOption}
    />
  );
};

export const SelectWithSearchRenderer = withJsonFormsControlProps(SelectWithSearch);

export const SelectWithSearchTester = rankWith(
  102,
  schemaMatches((schema) => schema && (schema.oneOf || schema.enum) && schema.searchable)
);

export default withJsonFormsControlProps(SelectRenderer);
